import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useLeaveOnSponsorFailure } from '../hooks/useLeaveOnSponsorFailure';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupApi } from '../hooks/useSignupApi';
import { useActiveSignupSponsor } from '../hooks/useActiveSignupSponsor';
import { useLinkIncentive } from '../hooks/useLinkIncentive';
import { useSignupProgramOptions } from '../hooks/useSignupProgramOptions';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { selectedProgramsUnavailable, toWrapUpContext, type WrapUpContext } from './branching';

/** The HDHP notice read's cache key, for readers that only look in the cache. */
export function hdhpNoticeQueryKey(scenario: string | null) {
  return ['signup', 'hdhp-notice', scenario] as const;
}

/** The registration profile read's cache key, for readers that only look in the cache. */
export function registrationProfileQueryKey(scenario: string | null) {
  return ['signup', 'registration-profile', scenario] as const;
}

export type WrapUpContextState = { ready: false } | { ready: true; context: WrapUpContext };

export interface WrapUpContextOptions {
  /**
   * Whether the HDHP notice may be read yet. The server answers false until
   * the member's main program is enrolled, so a caller that is still saving
   * that enrollment holds the read back until the save lands. Defaults to
   * true: the later wrap-up screens only open once it has.
   */
  hdhpNoticeReady?: boolean;
}

/**
 * The sponsor, HDHP and registration profile reads the wrap-up branches on,
 * with the member's chosen programs, as one context. The queries are cached
 * per scenario, so screens after the router reuse the router's answers; a
 * reload reads them again. A failed HDHP or profile read counts as off (a
 * failed profile read routes as an adult). A failed sponsor read on an
 * employer link goes to the error screen and the context never becomes ready;
 * with no employer named, or one picked in search that the lookup cannot
 * resolve, the switches are off. The chosen programs count as closed only
 * against the profile's age list; a failed options read, or a selection lost
 * to a reload, closes none.
 */
export function useWrapUpContext({
  hdhpNoticeReady = true,
}: WrapUpContextOptions = {}): WrapUpContextState {
  const { clientId } = useLegacySignupParams();
  const sponsor = useActiveSignupSponsor();
  // Only the employer a link names sends a failed lookup to the error screen.
  // An employer picked in search may be a decoy, which must not be told apart.
  const sponsorFailed = useLeaveOnSponsorFailure(sponsor, clientId);
  const api = useSignupApi();
  const scenario = useSearchParams().get('scenario');
  const hdhp = useQuery({
    queryKey: hdhpNoticeQueryKey(scenario),
    queryFn: () => api.getHdhpNotice(),
    enabled: hdhpNoticeReady,
    retry: false,
  });
  const profile = useQuery({
    queryKey: registrationProfileQueryKey(scenario),
    queryFn: () => api.getRegistrationProfile(),
    retry: false,
  });
  const options = useSignupProgramOptions();
  const selection = useSignupFlowStore((state) => state.programSelection);
  const incentive = useLinkIncentive();
  if (
    sponsorFailed ||
    sponsor.isPending ||
    hdhp.isPending ||
    profile.isPending ||
    options.isPending
  )
    return { ready: false };
  const programsUnavailable = selectedProgramsUnavailable(
    options.data?.options ?? [],
    selection,
    profile.data?.ineligibleProgramKeys ?? [],
  );
  return {
    ready: true,
    context: toWrapUpContext(
      sponsor.data?.features,
      hdhp.data?.requiresHdhpNotice,
      profile.data?.minorStatus,
      programsUnavailable,
      incentive,
    ),
  };
}
